import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2, RotateCcw, AlertTriangle, Printer, Wand2, FileDown, Link2, Eraser, X } from 'lucide-react';
import jsPDF from 'jspdf';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { NumberInput } from '../components/ui/number-input';
import { UnderlineTabs } from '../components/ui/underline-tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { useAutoSave } from '../hooks/useAutoSave';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import { Slot, couvre, presentLeJour, libelleDisponibilite } from '../components/volunteers/disponibilite';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import { toast } from 'sonner';
import { openPrintableSheet, SheetRow, SheetCell } from '../services/printable-sheet';
import { slugify } from '../lib/tableExport';

/**
 * Planning des juges (LOG-214) — CrossFit.
 *
 * ⚠️ C'est un DOCUMENT, pas une affectation : rien dans le scoring ne lit ce
 * plan. Un couloir sans juge n'est pas une erreur, aucune heat n'est bloquée.
 *
 * Deux lectures du même plan (2026-09-27, maquette validée par Bryan) :
 *
 * - **Par juge** (défaut) : une ligne par juge, le temps de gauche à droite,
 *   un bloc = un couloir tenu sur des heats consécutives, et le seul texte du
 *   bloc est le NUMÉRO de couloir. C'est le tableau de service qu'on lit au
 *   mur : « qui travaille quand », la charge et les pauses se voient sur la
 *   ligne. La grille par heat coupait les prénoms à trois lettres dès dix
 *   couloirs et répétait « heats 1→4 » dans chaque bloc.
 * - **Par heat** : lignes = heats, colonnes = couloirs — la réponse à « qui est
 *   sur le couloir 6 à 09:19 ». Prénom en gras, nom en dessous, la plage en bas
 *   du bloc seulement. C'est là que vivent le glisser-déposer et l'étirement.
 *
 * Le même tableau se partage aux juges par un lien (page publique, lecture
 * seule, en-têtes et première colonne fixes, filtre par nom et par WOD).
 */

interface Heat {
  id: string;
  heat_number: number;
  heat_date: string | null;
  start_time: string | null;
  lanes: number | null;
  wod_name: string | null;
  wod_number: number | null;
  category_name: string | null;
}
interface Volunteer {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  answers: Record<string, any> | null;
  role: string | null;
  /** Créneaux de présence. `null` = non renseigné, ce qui n'est PAS « absent ». */
  available_slots: Slot[] | null;
}
interface Assignment { heat_id: string; lane_number: number; volunteer_id: string }

/**
 * Couleurs d'identité des juges — reconnaître une PERSONNE d'un coup d'œil.
 * Validées avec l'outil de la charte (`dataviz`) sur le fond sombre, en toutes
 * paires : vision normale ΔE 15,1 · daltonisme ΔE 7,7 · contraste ≥ 3:1. Six
 * et pas douze : au-delà la pire paire tombe à ΔE 7. Ne pas y toucher sans
 * repasser le validateur. La page publique (logfit-public) porte la même liste.
 */
const COLORS = ['#007bd5', '#1fa3a4', '#1a813f', '#d93a45', '#7e5999', '#9c9800'];

const key = (heatId: string, lane: number) => `${heatId}::${lane}`;

const hex = (h: string): [number, number, number] =>
  [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
/** La teinte d'un juge éclaircie pour le papier : le texte reste noir dessus. */
const teinteClaire = (h: string): [number, number, number] =>
  hex(h).map((v) => Math.round(255 - (255 - v) * 0.22)) as [number, number, number];

type Vue = 'judge' | 'heat';

const JudgePlan: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedEventId, selectedEvent } = useEvent();

  const [loading, setLoading] = useState(false);
  const [heats, setHeats] = useState<Heat[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [plan, setPlan] = useState<Record<string, string>>({});
  // La grille telle que l'écran la tient : `commit` la lit ici, pas dans une
  // closure qui pourrait être en retard d'un rendu.
  const planRef = useRef<Record<string, string>>({});
  planRef.current = plan;
  const [dragged, setDragged] = useState<string | null>(null);
  const [vue, setVue] = useState<Vue>('judge');

  const load = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const d = await apiService.getJudgePlan(selectedEventId);
      setHeats(d.heats || []);
      setVolunteers(d.volunteers || []);
      const p: Record<string, string> = {};
      (d.assignments || []).forEach((a: Assignment) => { p[key(a.heat_id, a.lane_number)] = a.volunteer_id; });
      setPlan(p);
      setShowWodNames(!!d.share?.show_wod_names);
    } catch {
      toast.error(t('judgePlan.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, t]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const vus = new Set<string>();
    heats.forEach((h) => vus.add(h.heat_date || ''));
    return Array.from(vus).sort();
  }, [heats]);

  const [day, setDay] = useState<string>('');
  useEffect(() => {
    if (days.length && !days.includes(day)) setDay(days[0]);
  }, [days, day]);

  const dayHeats = useMemo(
    () => (days.length <= 1 ? heats : heats.filter((h) => (h.heat_date || '') === day)),
    [heats, days, day]);

  const dayLabel = (d: string) =>
    d ? new Date(`${d}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })
      : t('judgePlan.noDate');

  const hasRoles = useMemo(() => volunteers.some((v) => v.role), [volunteers]);
  const juges = useMemo(
    () => (hasRoles ? volunteers.filter((v) => v.role === 'judge') : volunteers),
    [volunteers, hasRoles]);

  const presentCeJour = useCallback((v: Volunteer) => !day || presentLeJour(v.available_slots, day), [day]);
  const disponiblePour = useCallback((v: Volunteer, h: Heat) =>
    couvre(v.available_slots, h.heat_date || day, h.start_time), [day]);

  const shown = useMemo(() => juges.filter(presentCeJour), [juges, presentCeJour]);
  const masques = juges.length - shown.length;

  const parId = useMemo(() => {
    const m = new Map<string, { v: Volunteer; couleur: string }>();
    // Rang parmi les JUGES : une couleur suit la personne, jamais son rang du moment.
    volunteers.forEach((v) => {
      const rang = juges.indexOf(v);
      const i = rang >= 0 ? rang : juges.length + volunteers.indexOf(v);
      m.set(v.id, { v, couleur: COLORS[i % COLORS.length] });
    });
    return m;
  }, [volunteers, juges]);

  const colorOf = (id: string) => parId.get(id)?.couleur || COLORS[0];
  const nameOf = (id: string) => {
    const v = parId.get(id)?.v;
    return v ? `${v.first_name} ${v.last_name}`.trim() : '—';
  };
  const absentCeJour = (id: string) => { const v = parId.get(id)?.v; return v ? !presentCeJour(v) : false; };
  const absentPour = (id: string, h: Heat) => { const v = parId.get(id)?.v; return v ? !disponiblePour(v, h) : false; };
  const motifAbsence = (id: string) => {
    const v = parId.get(id)?.v;
    return t('judgePlan.notAvailableAt', { name: nameOf(id), slots: libelleDisponibilite(v?.available_slots, i18n.language) || '—' });
  };

  /* ─────────── redimensionnement (vue par heat) ─────────── */
  const [resize, setResize] = useState<{ lane: number; topIdx: number; vid: string; toIdx: number } | null>(null);
  const resizeRef = useRef<typeof resize>(null);
  resizeRef.current = resize;
  const lignes = useRef<(HTMLTableRowElement | null)[]>([]);
  const ligneSous = (clientY: number): number | null => {
    for (let i = 0; i < lignes.current.length; i++) {
      const r = lignes.current[i]?.getBoundingClientRect();
      if (r && clientY >= r.top && clientY <= r.bottom) return i;
    }
    return null;
  };
  const enRedimension = resize !== null;
  useEffect(() => {
    if (!enRedimension) return;
    const { userSelect, cursor } = document.body.style;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    const suivre = (e: MouseEvent) => {
      const idx = ligneSous(e.clientY);
      if (idx === null) return;
      setResize((r) => (r && r.toIdx !== idx ? { ...r, toIdx: idx } : r));
    };
    const fin = () => {
      const r = resizeRef.current;
      if (r) ajusterBloc(r.topIdx, r.lane, r.vid, r.toIdx);
      setResize(null);
    };
    window.addEventListener('mousemove', suivre);
    window.addEventListener('mouseup', fin);
    return () => {
      window.removeEventListener('mousemove', suivre);
      window.removeEventListener('mouseup', fin);
      document.body.style.userSelect = userSelect;
      document.body.style.cursor = cursor;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enRedimension]);

  const loadOf = (id: string) => {
    const order = dayHeats.map((h) => h.id);
    const duJour = new Set(order);
    const held = new Set(
      Object.entries(planAffiche).filter(([, vid]) => vid === id)
        .map(([k]) => k.split('::')[0]).filter((hid) => duJour.has(hid)));
    let best = 0, run = 0;
    order.forEach((hid) => { run = held.has(hid) ? run + 1 : 0; best = Math.max(best, run); });
    return { total: held.size, streak: best };
  };

  const place = (heatId: string, lane: number, volunteerId: string) => {
    // Ce garde ne refuse que l'absence de toute la journée ; à l'heure près on
    // POSE quand même, en ambre : l'organisateur peut savoir mieux que le formulaire.
    if (absentCeJour(volunteerId)) { toast.error(motifAbsence(volunteerId)); return; }
    commit((prev) => {
      const next = { ...prev };
      // Un juge ne tient JAMAIS deux couloirs sur la même heat.
      Object.keys(next).forEach((k) => { if (k.startsWith(`${heatId}::`) && next[k] === volunteerId) delete next[k]; });
      next[key(heatId, lane)] = volunteerId;
      return next;
    });
  };

  const clearRun = (fromIdx: number, height: number, lane: number) => {
    commit((prev) => {
      const n = { ...prev };
      for (let i = fromIdx; i < fromIdx + height && i < dayHeats.length; i++) delete n[key(dayHeats[i].id, lane)];
      return n;
    });
  };

  /** Vue par juge : un clic sur une case vide place le juge sur le premier
   *  couloir libre de la heat ; un clic sur son bloc l'en retire. */
  const basculer = (vid: string, h: Heat) => {
    const couloirs = Array.from({ length: h.lanes || 0 }, (_, i) => i + 1);
    const tenu = couloirs.find((l) => plan[key(h.id, l)] === vid);
    if (tenu) {
      commit((prev) => { const n = { ...prev }; delete n[key(h.id, tenu)]; return n; });
      return;
    }
    const libre = couloirs.find((l) => !plan[key(h.id, l)]);
    if (!libre) { toast.error(t('judgePlan.heatFull', { heat: h.heat_number })); return; }
    place(h.id, libre, vid);
  };

  const avecAjustement = useCallback((
    base: Record<string, string>, topIdx: number, lane: number, vid: string, toIdx: number,
  ) => {
    const next = { ...base };
    const fin = Math.max(topIdx, Math.min(toIdx, dayHeats.length - 1));
    for (let i = topIdx; i <= fin; i++) {
      const h = dayHeats[i];
      if (lane > (h.lanes || 0)) break;
      const occupant = next[key(h.id, lane)];
      if (occupant && occupant !== vid) break;
      const ailleurs = Object.keys(next).some((k) => k.startsWith(`${h.id}::`) && next[k] === vid && k !== key(h.id, lane));
      if (ailleurs) break;
      next[key(h.id, lane)] = vid;
    }
    for (let i = fin + 1; i < dayHeats.length; i++) {
      const k = key(dayHeats[i].id, lane);
      if (next[k] !== vid) break;
      delete next[k];
    }
    return next;
  }, [dayHeats]);

  const ajusterBloc = (topIdx: number, lane: number, vid: string, toIdx: number) => {
    if (absentCeJour(vid)) return;
    commit((prev) => avecAjustement(prev, topIdx, lane, vid, toIdx));
  };

  const planAffiche = useMemo(
    () => (resize ? avecAjustement(plan, resize.topIdx, resize.lane, resize.vid, resize.toIdx) : plan),
    [plan, resize, avecAjustement]);

  /* ─────────── sauvegarde automatique (2026-09-27) ───────────
   * La règle du 22/09 : ce qui rend public garde un geste explicite, le reste
   * se sauve tout seul. Ici, ce qui rend public c'est le LIEN juges, pas une
   * case du tableau : chaque placement, retrait, étirement, Vider ou
   * assignation automatique écrit tout de suite (le plan entier, comme
   * `PUT /judge-plan` l'exige). Les écritures sont mises en file, donc jamais
   * réordonnées par le réseau ; un refus remet la grille dans son état
   * d'avant et l'indicateur le dit. Les gestes de masse offrent « Annuler ». */
  const fileEcritures = useRef<Promise<unknown>>(Promise.resolve());
  const autoSave = useAutoSave<{ plan: Record<string, string> }>(
    async ({ plan: p }) => {
      if (!selectedEventId) return;
      const assignments = Object.entries(p).map(([k, volunteer_id]) => {
        const [heat_id, lane] = k.split('::');
        return { heat_id, lane_number: Number(lane), volunteer_id };
      });
      const suivante = fileEcritures.current.catch(() => undefined)
        .then(() => apiService.saveJudgePlan(selectedEventId, assignments));
      fileEcritures.current = suivante;
      await suivante;
    },
    { onError: () => toast.error(t('judgePlan.saveError')) },
  );

  /** Le seul point d'écriture : calcule le plan suivant, l'affiche, l'envoie. */
  const commit = (suivant: (prev: Record<string, string>) => Record<string, string>) => {
    const avant = planRef.current;
    const next = suivant(avant);
    setPlan(next);
    planRef.current = next;
    void autoSave.commit({ plan: next }, () => { setPlan(avant); planRef.current = avant; });
    return avant;
  };
  /** Un geste de masse se défait par un toast, pas par une confirmation avant. */
  const commitAnnulable = (suivant: (prev: Record<string, string>) => Record<string, string>, message: string) => {
    const avant = commit(suivant);
    toast.success(message, { action: { label: t('judgePlan.undo'), onClick: () => { commit(() => avant); } } });
  };

  const maxLanes = useMemo(() => Math.max(1, ...dayHeats.map((h) => h.lanes || 0)), [dayHeats]);

  /* ─────────── vider (2026-09-27, demande de Bryan) ───────────
   * Retirer d'un coup toutes les affectations, celles d'un juge ou celles d'un
   * couloir. Écrit tout de suite, avec « Annuler » sur le message ; seul « tout
   * le planning » demande confirmation avant : il touche des jours qu'on ne
   * voit pas. Le périmètre par défaut est la JOURNÉE affichée. */
  const dayHeatIds = useMemo(() => new Set(dayHeats.map((h) => h.id)), [dayHeats]);
  const entreesDuJour = useMemo(
    () => Object.entries(plan).filter(([k]) => dayHeatIds.has(k.split('::')[0])),
    [plan, dayHeatIds]);
  const jugesPlaces = useMemo(() => {
    const m = new Map<string, number>();
    entreesDuJour.forEach(([, vid]) => m.set(vid, (m.get(vid) || 0) + 1));
    return Array.from(m.entries())
      .map(([vid, n]) => ({ vid, n, name: nameOf(vid) }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entreesDuJour, parId]);
  const couloirsPlaces = useMemo(() => {
    const m = new Map<number, number>();
    entreesDuJour.forEach(([k]) => { const lane = Number(k.split('::')[1]); m.set(lane, (m.get(lane) || 0) + 1); });
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [entreesDuJour]);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const vider = (garder: (k: string, vid: string) => boolean, retires: number) => {
    commitAnnulable(
      (prev) => Object.fromEntries(Object.entries(prev).filter(([k, vid]) => garder(k, vid))),
      t('judgePlan.clear.done', { count: retires }));
  };
  const viderJournee = () => vider((k) => !dayHeatIds.has(k.split('::')[0]), entreesDuJour.length);
  const viderTout = () => vider(() => false, Object.keys(plan).length);
  const viderJuge = (vid: string, n: number) => vider((k, v) => !(v === vid && dayHeatIds.has(k.split('::')[0])), n);
  const viderCouloir = (lane: number, n: number) =>
    vider((k) => !(Number(k.split('::')[1]) === lane && dayHeatIds.has(k.split('::')[0])), n);

  const juesAvertis = useMemo(() => {
    const avertis = new Set<string>();
    dayHeats.forEach((h) => {
      for (let lane = 1; lane <= maxLanes; lane++) {
        const vid = planAffiche[key(h.id, lane)];
        if (vid && absentPour(vid, h)) avertis.add(vid);
      }
    });
    return Array.from(avertis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayHeats, plan, maxLanes, volunteers, day]);

  /** Blocs de la vue par heat : par couloir, les suites de heats consécutives
   *  tenues par le même juge, coupées au changement de WOD. 0 = ligne couverte. */
  const spans = useMemo(() => {
    const m = new Map<string, number>();
    for (let lane = 1; lane <= maxLanes; lane++) {
      let i = 0;
      while (i < dayHeats.length) {
        const vid = planAffiche[key(dayHeats[i].id, lane)];
        if (!vid) { i++; continue; }
        let j = i + 1;
        while (j < dayHeats.length && dayHeats[j].wod_number === dayHeats[i].wod_number
               && planAffiche[key(dayHeats[j].id, lane)] === vid) j++;
        m.set(key(dayHeats[i].id, lane), j - i);
        for (let k = i + 1; k < j; k++) m.set(key(dayHeats[k].id, lane), 0);
        i = j;
      }
    }
    return m;
  }, [dayHeats, planAffiche, maxLanes]);

  /** Vue par juge : le couloir tenu par un juge sur une heat, 0 sinon. */
  const laneDe = useCallback((vid: string, h: Heat): number => {
    for (let l = 1; l <= (h.lanes || 0); l++) if (planAffiche[key(h.id, l)] === vid) return l;
    return 0;
  }, [planAffiche]);

  /** Bandeaux de WOD (vue par juge). */
  const bandeaux = useMemo(() => {
    const out: { nom: string; n: number }[] = [];
    dayHeats.forEach((h, i) => {
      const prev = dayHeats[i - 1];
      if (!prev || prev.wod_number !== h.wod_number) out.push({ nom: h.wod_name || t('judgePlan.noWod'), n: 1 });
      else out[out.length - 1].n++;
    });
    return out;
  }, [dayHeats, t]);

  /** Les lignes de la vue par juge : les juges du jour, plus ceux placés bien qu'absents. */
  const lignesJuges = useMemo(() => {
    const ids = new Set(shown.map((v) => v.id));
    Object.entries(planAffiche).forEach(([k, vid]) => {
      if (dayHeats.some((h) => h.id === k.split('::')[0])) ids.add(vid);
    });
    return Array.from(ids).map((id) => parId.get(id)?.v).filter((v): v is Volunteer => !!v);
  }, [shown, planAffiche, dayHeats, parId]);

  /* ─────────── assignation automatique ─────────── */
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoJuges, setAutoJuges] = useState<Set<string>>(new Set());
  const [restAfter, setRestAfter] = useState(4);
  const [restHeats, setRestHeats] = useState(1);
  const [autoBusy, setAutoBusy] = useState(false);

  const ouvrirAuto = () => { setAutoJuges(new Set(shown.map((v) => v.id))); setAutoOpen(true); };

  const lancerAuto = async () => {
    if (!selectedEventId || autoJuges.size === 0) return;
    setAutoBusy(true);
    try {
      const assignments = Object.entries(plan).map(([k, volunteer_id]) => {
        const [heat_id, lane] = k.split('::');
        return { heat_id, lane_number: Number(lane), volunteer_id };
      });
      const r = await apiService.autoAssignJudgePlan(selectedEventId, {
        volunteer_ids: Array.from(autoJuges), assignments,
        day: days.length > 1 ? day : null, rest_after: restAfter, rest_heats: restHeats,
      });
      const next: Record<string, string> = {};
      r.assignments.forEach((a) => { next[key(a.heat_id, a.lane_number)] = a.volunteer_id; });
      setAutoOpen(false);
      // La proposition ne touche qu'aux couloirs VIDES : on l'applique et on
      // l'écrit, « Annuler » rend la grille d'avant.
      if (r.added > 0) commitAnnulable(() => next, t('judgePlan.autoDone', { count: r.added }));
      else toast.info(t('judgePlan.autoNothing'));
      if (r.uncovered.length) {
        const nums = r.uncovered.map((u) => heats.find((h) => h.id === u.heat_id)?.heat_number)
          .filter((n): n is number => n !== undefined);
        const total = r.uncovered.reduce((n, u) => n + u.lanes.length, 0);
        toast.warning(t('judgePlan.autoUncovered', { count: total, heats: nums.join(', ') }));
      }
    } catch {
      toast.error(t('judgePlan.saveError'));
    } finally {
      setAutoBusy(false);
    }
  };

  /* ─────────── lien à partager aux juges ─────────── */
  const [shareBusy, setShareBusy] = useState(false);
  // Noms des WODs sur la page des juges (2026-09-27) : faux par défaut, le
  // lien part avant l'événement. Interrupteur écrit au clic (doctrine
  // auto-save), retour en arrière si le serveur refuse.
  const [showWodNames, setShowWodNames] = useState(false);
  const basculerNomsWods = async (v: boolean) => {
    if (!selectedEventId) return;
    setShowWodNames(v);
    try {
      await apiService.setJudgePlanShare(selectedEventId, v);
      toast.success(t(v ? 'judgePlan.wodNamesShown' : 'judgePlan.wodNamesHidden'));
    } catch {
      setShowWodNames(!v);
      toast.error(t('judgePlan.saveError'));
    }
  };
  const partager = async () => {
    if (!selectedEventId) return;
    setShareBusy(true);
    try {
      const r = await apiService.shareJudgePlan(selectedEventId);
      try {
        await navigator.clipboard.writeText(r.url);
        toast.success(t('judgePlan.shareCopied'));
      } catch {
        window.prompt(t('judgePlan.shareLink'), r.url);
      }
    } catch {
      toast.error(t('judgePlan.shareError'));
    } finally {
      setShareBusy(false);
    }
  };

  /* ─────────── impression (feuille HTML) ─────────── */
  const imprimer = () => {
    const rows: SheetRow[] = [];
    let wodCourant: number | null = null;
    dayHeats.forEach((h) => {
      if (h.wod_number !== wodCourant) {
        wodCourant = h.wod_number ?? null;
        rows.push({ band: h.wod_name || t('judgePlan.noWod') });
      }
      const cells: SheetCell[] = [{ v: `${(h.start_time || '').slice(0, 5)}`, sub: `${t('judgePlan.heat')} ${h.heat_number}`, cls: 'hour' }];
      for (let lane = 1; lane <= maxLanes; lane++) {
        const span = spans.get(key(h.id, lane));
        if (span === 0) continue;
        const vid = planAffiche[key(h.id, lane)];
        if (lane > (h.lanes || 0)) { cells.push({ v: '', cls: 'dim' }); continue; }
        cells.push(vid ? { v: nameOf(vid), rowSpan: span || 1 } : { v: '—', cls: 'dim' });
      }
      rows.push({ cells });
    });
    openPrintableSheet({
      title: t('judgePlan.title'),
      subtitle: [selectedEvent?.name, days.length > 1 ? dayLabel(day) : null].filter(Boolean).join(' · '),
      landscape: true,
      sections: [{ grid: true,
        columns: [{ label: t('judgePlan.hour'), width: '20mm', cls: 'hour' },
                  ...Array.from({ length: maxLanes }, (_, i) => ({ label: `${t('judgePlan.laneShort')}${i + 1}` }))],
        rows }],
      footer: t('heats.printFooter'), printLabel: t('heats.print'), closeLabel: t('common.close'),
    });
  };

  /* ─────────── PDF en couleurs, tel qu'affiché ─────────── */
  const [pdfBusy, setPdfBusy] = useState(false);

  /**
   * Le PDF reproduit la vue affichée : les mêmes blocs (`colSpan` par juge,
   * `rowSpan` par heat) et la couleur de chaque juge en fond clair, texte
   * noir, bordure à sa teinte — la même identité que l'écran, adaptée au
   * papier blanc. Beaucoup de heats ou de couloirs → A3, sinon A4, en paysage.
   */
  const exporterPdf = async () => {
    setPdfBusy(true);
    try {
      const { default: autoTable } = await import('jspdf-autotable');
      const format = (vue === 'judge' ? dayHeats.length > 30 : maxLanes > 10) ? 'a3' : 'a4';
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format });
      const largeur = doc.internal.pageSize.getWidth();
      const sous = [selectedEvent?.name, days.length > 1 ? dayLabel(day) : null].filter(Boolean).join(' · ');
      doc.setFontSize(14); doc.text(t('judgePlan.title'), 10, 12);
      doc.setFontSize(9); doc.setTextColor(110); doc.text(sous, 10, 17); doc.setTextColor(0);

      type Cellule = { content: string; colSpan?: number; rowSpan?: number; styles?: Record<string, unknown> };
      const style = (vid: string | null, warn = false): Record<string, unknown> => vid ? {
        fillColor: warn ? [255, 226, 180] : teinteClaire(colorOf(vid)),
        lineColor: hex(colorOf(vid)), lineWidth: 0.35, textColor: [20, 20, 20],
        fontStyle: 'bold', halign: 'center', valign: 'middle',
      } : { textColor: [170, 170, 170], halign: 'center' };

      if (vue === 'judge') {
        const head: Cellule[][] = [
          [{ content: t('judgePlan.judge'), rowSpan: 2, styles: { halign: 'left', valign: 'bottom' } },
           ...bandeaux.map((b) => ({ content: b.nom, colSpan: b.n, styles: { halign: 'left', fontStyle: 'bold' } }))],
          dayHeats.map((h) => ({ content: `${(h.start_time || '').slice(0, 5)}\nH${h.heat_number}`, styles: { fontSize: 6 } })),
        ];
        const body: Cellule[][] = lignesJuges.map((v) => {
          const { total, streak } = loadOf(v.id);
          const row: Cellule[] = [{
            content: `${v.first_name} ${v.last_name}\n${total ? `${total} ${t('judgePlan.heatsShort')} · ${t('judgePlan.streak', { count: streak })}` : t('judgePlan.unassigned')}`,
            styles: { halign: 'left', fontStyle: 'bold', fontSize: 7 },
          }];
          let i = 0;
          while (i < dayHeats.length) {
            const l = laneDe(v.id, dayHeats[i]);
            if (!l) { row.push({ content: '', styles: style(null) }); i++; continue; }
            let j = i + 1;
            while (j < dayHeats.length && laneDe(v.id, dayHeats[j]) === l
                   && dayHeats[j].wod_number === dayHeats[i].wod_number) j++;
            const warn = dayHeats.slice(i, j).some((h) => absentPour(v.id, h));
            row.push({ content: `${warn ? '! ' : ''}${t('judgePlan.laneShort')}${l}`, colSpan: j - i, styles: style(v.id, warn) });
            i = j;
          }
          return row;
        });
        autoTable(doc, {
          startY: 21, head, body, theme: 'grid',
          styles: { fontSize: 7, cellPadding: 1, lineColor: [210, 210, 210], lineWidth: 0.15, overflow: 'linebreak' },
          headStyles: { fillColor: [240, 240, 240], textColor: [40, 40, 40], fontSize: 7 },
          columnStyles: { 0: { cellWidth: 42 } },
          tableWidth: largeur - 20, margin: { left: 10, right: 10 },
        });
      } else {
        const head: Cellule[][] = [[
          { content: t('judgePlan.heat'), styles: { halign: 'left' } },
          ...Array.from({ length: maxLanes }, (_, i) => ({ content: `${t('judgePlan.laneShort')}${i + 1}` })),
        ]];
        const body: Cellule[][] = [];
        let wodCourant: number | null = null;
        dayHeats.forEach((h) => {
          if (h.wod_number !== wodCourant) {
            wodCourant = h.wod_number ?? null;
            body.push([{ content: h.wod_name || t('judgePlan.noWod'), colSpan: maxLanes + 1,
                         styles: { fillColor: [240, 240, 240], fontStyle: 'bold', halign: 'left' } }]);
          }
          const row: Cellule[] = [{
            content: `${t('judgePlan.heat')} ${h.heat_number} · ${(h.start_time || '').slice(0, 5)}\n${h.category_name || ''}`,
            styles: { halign: 'left', fontSize: 7 },
          }];
          for (let lane = 1; lane <= maxLanes; lane++) {
            const span = spans.get(key(h.id, lane));
            if (span === 0) continue;
            const vid = planAffiche[key(h.id, lane)];
            if (lane > (h.lanes || 0)) { row.push({ content: '', styles: { fillColor: [245, 245, 245] } }); continue; }
            if (!vid) { row.push({ content: '—', styles: style(null) }); continue; }
            const warn = absentPour(vid, h);
            const v = parId.get(vid)?.v;
            row.push({ content: `${warn ? '! ' : ''}${v?.first_name || ''}\n${(v?.last_name || '').toUpperCase()}`,
                       rowSpan: span || 1, styles: style(vid, warn) });
          }
          body.push(row);
        });
        autoTable(doc, {
          startY: 21, head, body, theme: 'grid',
          styles: { fontSize: 7.5, cellPadding: 1.2, lineColor: [210, 210, 210], lineWidth: 0.15, overflow: 'linebreak' },
          headStyles: { fillColor: [240, 240, 240], textColor: [40, 40, 40] },
          columnStyles: { 0: { cellWidth: 34 } },
          tableWidth: largeur - 20, margin: { left: 10, right: 10 },
        });
      }
      doc.save(`${slugify(t('judgePlan.title'))}-${slugify(selectedEvent?.name || 'event')}${days.length > 1 ? `-${day}` : ''}.pdf`);
    } catch {
      toast.error(t('judgePlan.pdfError'));
    } finally {
      setPdfBusy(false);
    }
  };

  if (!selectedEventId) {
    return <div className="p-6 text-muted-foreground">{t('judgePlan.selectEvent')}</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {t('judgePlan.context', { heats: dayHeats.length, judges: shown.length })}
        </span>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RotateCcw size={15} className="mr-1.5" />{t('common.refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={imprimer} disabled={loading}>
            <Printer size={15} className="mr-1.5" />{t('heats.print')}
          </Button>
          <Button variant="outline" size="sm" onClick={exporterPdf} disabled={loading || pdfBusy || dayHeats.length === 0}>
            {pdfBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileDown size={15} className="mr-1.5" />}
            {t('judgePlan.pdf')}
          </Button>
          <Button variant="outline" size="sm" onClick={partager} disabled={loading || shareBusy}
                  title={t('judgePlan.shareHint')}>
            {shareBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Link2 size={15} className="mr-1.5" />}
            {t('judgePlan.share')}
          </Button>
          <Button variant="outline" size="sm" onClick={ouvrirAuto}
                  disabled={loading || dayHeats.length === 0 || shown.length === 0}>
            <Wand2 size={15} className="mr-1.5" />{t('judgePlan.autoAssign')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading || Object.keys(plan).length === 0}>
                <Eraser size={15} className="mr-1.5" />{t('judgePlan.clear.button')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem disabled={entreesDuJour.length === 0} onClick={viderJournee}>
                {days.length > 1 ? t('judgePlan.clear.day', { count: entreesDuJour.length }) : t('judgePlan.clear.all', { count: entreesDuJour.length })}
              </DropdownMenuItem>
              {days.length > 1 && (
                <DropdownMenuItem onClick={() => setConfirmClearAll(true)}>
                  {t('judgePlan.clear.allDays', { count: Object.keys(plan).length })}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger disabled={jugesPlaces.length === 0}>{t('judgePlan.clear.byJudge')}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                  {jugesPlaces.map(({ vid, n, name }) => (
                    <DropdownMenuItem key={vid} onClick={() => viderJuge(vid, n)}>
                      <span aria-hidden className="mr-2 h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf(vid) }} />
                      <span className="flex-1 truncate">{name}</span>
                      <span className="ml-3 tabular-nums text-xs text-muted-foreground">{n}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger disabled={couloirsPlaces.length === 0}>{t('judgePlan.clear.byLane')}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {couloirsPlaces.map(([lane, n]) => (
                    <DropdownMenuItem key={lane} onClick={() => viderCouloir(lane, n)}>
                      <span className="flex-1">{t('judgePlan.clear.lane', { lane })}</span>
                      <span className="ml-3 tabular-nums text-xs text-muted-foreground">{n}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} className="self-center" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {vue === 'judge' ? t('judgePlan.hintByJudge') : t('judgePlan.hint')}
      </p>

      <label className="flex items-center gap-3 text-sm">
        <Switch checked={showWodNames} onCheckedChange={basculerNomsWods} disabled={loading} />
        <span>
          {t('judgePlan.wodNames')}
          <span className="ml-2 text-xs text-muted-foreground">{t('judgePlan.wodNamesHint')}</span>
        </span>
      </label>

      {juges.length > 0 && shown.length === 0 && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle size={12} className="shrink-0" />{t('judgePlan.dayOutsideEvent')}
        </p>
      )}
      {juesAvertis.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-warning">
          <AlertTriangle size={12} className="shrink-0" />
          {t('judgePlan.legendNotAvailable', { count: juesAvertis.length })}
        </p>
      )}

      {days.length > 1 && (
        <div className="flex gap-1 border-b overflow-x-auto">
          {days.map((d) => (
            <button key={d || 'sans-date'} type="button" onClick={() => setDay(d)}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                d === day ? 'border-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {dayLabel(d)}
              <span className="ml-1.5 text-xs text-muted-foreground">
                {heats.filter((h) => (h.heat_date || '') === d).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Deux lectures du même plan : un sélecteur est un onglet. */}
      <UnderlineTabs
        size="sm" value={vue} onChange={(v) => setVue(v as Vue)}
        items={[{ value: 'judge', label: t('judgePlan.viewByJudge') }, { value: 'heat', label: t('judgePlan.viewByHeat') }]}
        aria-label={t('judgePlan.title')}
      />

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
      ) : vue === 'judge' ? (

        /* ─────────── vue PAR JUGE ─────────── */
        <div className="overflow-auto rounded-xl border max-h-[75vh]">
          <table className="border-collapse text-xs min-w-full">
            <thead className="sticky top-0 z-20 bg-background">
              <tr>
                <th rowSpan={2}
                    className="sticky left-0 z-30 bg-background border-r border-b px-3 py-2 text-left
                               text-[11px] uppercase tracking-wide text-muted-foreground font-bold min-w-[210px]">
                  {t('judgePlan.judges')} · {lignesJuges.length}
                  {masques > 0 && (
                    <span className="block normal-case tracking-normal font-normal text-[11px]">
                      {t('judgePlan.hiddenThatDay', { count: masques })}
                    </span>
                  )}
                </th>
                {bandeaux.map((b, i) => (
                  <th key={i} colSpan={b.n}
                      className="px-2 py-1.5 text-left font-semibold bg-muted/40 border-l border-b whitespace-nowrap">
                    {b.nom}
                  </th>
                ))}
              </tr>
              <tr>
                {dayHeats.map((h, i) => {
                  const prev = dayHeats[i - 1];
                  const debutWod = !prev || prev.wod_number !== h.wod_number;
                  return (
                    <th key={h.id} title={h.category_name || ''}
                        className={`min-w-[52px] px-1 py-1 text-center font-semibold tabular-nums border-b
                                    ${debutWod ? 'border-l' : 'border-l border-border/40'}`}>
                      {(h.start_time || '').slice(0, 5)}
                      <span className="block text-[10px] font-normal text-muted-foreground">H{h.heat_number}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {lignesJuges.map((v) => {
                const { total, streak } = loadOf(v.id);
                const teinte = colorOf(v.id);
                const cells: React.ReactNode[] = [];
                let i = 0;
                while (i < dayHeats.length) {
                  const h = dayHeats[i];
                  const l = laneDe(v.id, h);
                  const prev = dayHeats[i - 1];
                  const debutWod = !prev || prev.wod_number !== h.wod_number;
                  if (!l) {
                    cells.push(
                      <td key={h.id} onClick={() => basculer(v.id, h)}
                          title={t('judgePlan.clickToPlace', { heat: h.heat_number })}
                          className={`h-[34px] p-0.5 cursor-pointer hover:bg-muted/40 ${debutWod ? 'border-l' : 'border-l border-border/40'}`} />,
                    );
                    i++; continue;
                  }
                  let j = i + 1;
                  while (j < dayHeats.length && laneDe(v.id, dayHeats[j]) === l
                         && dayHeats[j].wod_number === dayHeats[i].wod_number) j++;
                  const warn = dayHeats.slice(i, j).some((hh) => absentPour(v.id, hh));
                  cells.push(
                    <td key={h.id} colSpan={j - i} className={`h-[34px] p-0.5 ${debutWod ? 'border-l' : 'border-l border-border/40'}`}>
                      <div onClick={() => basculer(v.id, dayHeats[j - 1])}
                           title={warn ? motifAbsence(v.id) : t('judgePlan.clickToRemove', { heat: dayHeats[j - 1].heat_number })}
                           className="h-full rounded-[3px] border flex items-center justify-center gap-1 font-bold text-[13px] cursor-pointer select-none"
                           style={{ borderColor: teinte, backgroundColor: warn ? 'rgba(217,119,6,0.18)' : `${teinte}2E` }}>
                        {warn && <AlertTriangle size={11} className="text-warning shrink-0" />}
                        {t('judgePlan.laneShort')}{l}
                        {j - i > 1 && <span className="font-normal text-[10px] text-muted-foreground">· {j - i}</span>}
                      </div>
                    </td>,
                  );
                  i = j;
                }
                return (
                  <tr key={v.id} className="border-t">
                    <td className="sticky left-0 z-10 bg-background border-r px-3 py-1 whitespace-nowrap">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 align-[1px]" style={{ background: teinte }} />
                      <span className="font-semibold text-[13px]">{v.first_name} {v.last_name}</span>
                      <span className={`block text-[11px] tabular-nums ${total ? 'text-muted-foreground' : 'text-warning'}`}>
                        {total ? `${total} ${t('judgePlan.heatsShort')} · ${t('judgePlan.streak', { count: streak })}` : t('judgePlan.unassigned')}
                      </span>
                    </td>
                    {cells}
                  </tr>
                );
              })}
              {lignesJuges.length === 0 && (
                <tr><td colSpan={dayHeats.length + 1} className="py-10 text-center text-muted-foreground">{t('judgePlan.noJudges')}</td></tr>
              )}
            </tbody>
          </table>
        </div>

      ) : (

        /* ─────────── vue PAR HEAT ─────────── */
        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-4 items-start">
          <div className="rounded-xl border p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold">
              {t('judgePlan.judges')} · {shown.length}
            </div>
            {masques > 0 && <p className="mt-1.5 text-[11px] text-muted-foreground">{t('judgePlan.hiddenThatDay', { count: masques })}</p>}
            {!hasRoles && <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{t('judgePlan.noRolesYet')}</p>}
            <div className="mt-3 divide-y">
              {shown.map((v) => {
                const { total, streak } = loadOf(v.id);
                return (
                  <div key={v.id} draggable onDragStart={() => setDragged(v.id)} onDragEnd={() => setDragged(null)}
                       className="flex items-center gap-2 py-2 cursor-grab hover:bg-muted/40 rounded px-1">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorOf(v.id) }} />
                    <span className="flex-1 text-sm font-medium truncate">{v.first_name} {v.last_name}</span>
                    <span className={`text-[11px] text-right leading-tight ${total === 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                      <b className="text-foreground">{total}</b> {t('judgePlan.heatsShort')}<br />
                      {total === 0 ? t('judgePlan.unassigned') : t('judgePlan.streak', { count: streak })}
                    </span>
                  </div>
                );
              })}
              {shown.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">{t('judgePlan.noJudges')}</div>}
            </div>
          </div>

          <div className="overflow-auto rounded-xl border max-h-[75vh]">
            {/* Largeur de colonne GARANTIE : un nom doit tenir ; à dix couloirs
                la table dépasse et défile, plutôt que de couper les prénoms. */}
            <table className="border-collapse text-sm" style={{ minWidth: 150 + maxLanes * 122 }}>
              <thead className="sticky top-0 z-20 bg-background">
                <tr>
                  <th className="sticky left-0 z-30 bg-background px-3 py-2 text-[11px] uppercase tracking-wide
                                 text-muted-foreground font-bold border-r border-b w-[150px] text-left">
                    {t('judgePlan.heat')}
                  </th>
                  {Array.from({ length: maxLanes }, (_, i) => (
                    <th key={i} className="px-2 py-2 text-[11px] uppercase tracking-wide text-muted-foreground font-bold
                                           border-l border-b border-border/40 min-w-[122px]">
                      {t('judgePlan.laneShort')}{i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dayHeats.map((h, idx) => {
                  const prev = dayHeats[idx - 1];
                  const newWod = !prev || prev.wod_number !== h.wod_number;
                  return (
                    <React.Fragment key={h.id}>
                      {newWod && (
                        <tr className="bg-muted/40">
                          <td colSpan={maxLanes + 1} className="px-3 py-1.5 font-semibold">{h.wod_name || t('judgePlan.noWod')}</td>
                        </tr>
                      )}
                      <tr ref={(el) => { lignes.current[idx] = el; }} className="border-t">
                        <td className="sticky left-0 z-10 bg-background px-3 py-1.5 align-middle border-r">
                          <div className="font-medium">
                            {t('judgePlan.heat')} {h.heat_number}
                            {h.start_time && <span className="text-muted-foreground font-normal tabular-nums">{' · '}{String(h.start_time).slice(0, 5)}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground">{h.category_name}</div>
                        </td>
                        {Array.from({ length: maxLanes }, (_, i) => {
                          const lane = i + 1;
                          const exists = lane <= (h.lanes || 0);
                          const vid = planAffiche[key(h.id, lane)];
                          const span = spans.get(key(h.id, lane));
                          if (span === 0) return null;
                          if (!exists) return <td key={lane} className="bg-muted/20 border-l border-border/40" />;
                          const hauteur = span || 1;
                          const finIdx = idx + hauteur - 1;
                          const teinte = vid ? colorOf(vid) : undefined;
                          const v = vid ? parId.get(vid)?.v : undefined;
                          return (
                            <td key={lane} rowSpan={hauteur} style={{ height: 1 }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => dragged && place(h.id, lane, dragged)}
                                className="p-1 text-center align-middle border-l border-border/40">
                              {vid ? (
                                <div className="group relative h-full min-h-[46px] rounded-md px-2 py-1.5 text-xs flex flex-col justify-center border text-left leading-tight"
                                     title={absentPour(vid, h) ? motifAbsence(vid) : undefined}
                                     style={{ borderColor: teinte, backgroundColor: absentPour(vid, h) ? 'rgba(217,119,6,0.14)' : `${teinte}1A` }}>
                                  <div className="flex items-start gap-1">
                                    {absentPour(vid, h) && <AlertTriangle size={11} className="shrink-0 text-warning mt-0.5" aria-label={motifAbsence(vid)} />}
                                    <span className="flex-1 min-w-0">
                                      {/* Prénom en gras, nom en dessous : la place que la grille refusait. */}
                                      <span className="block font-bold truncate">{v?.first_name}</span>
                                      <span className="block text-[11px] uppercase tracking-wide text-muted-foreground truncate">{v?.last_name}</span>
                                    </span>
                                    <button title={t('judgePlan.remove')} onClick={() => clearRun(idx, hauteur, lane)}
                                            className="[@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-muted-foreground hover:text-destructive">
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                  {(() => {
                                    const enCours = resize?.lane === lane && resize?.topIdx === idx;
                                    if (hauteur <= 1 && !enCours) return null;
                                    return (
                                      <span className={`mt-auto text-[10px] tabular-nums ${enCours ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                                        → {t('judgePlan.heat').toLowerCase()} {dayHeats[finIdx].heat_number}
                                      </span>
                                    );
                                  })()}
                                  <div title={t('judgePlan.stretch')}
                                       onMouseDown={(e) => { e.preventDefault(); setResize({ lane, topIdx: idx, vid, toIdx: finIdx }); }}
                                       className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize [@media(hover:hover)]:opacity-0 group-hover:opacity-100 select-none flex items-center justify-center">
                                    <span className="h-[3px] w-6 rounded-full" style={{ backgroundColor: teinte }} />
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground/25">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  );
                })}
                {heats.length === 0 && (
                  <tr><td colSpan={maxLanes + 1} className="py-10 text-center text-muted-foreground">{t('judgePlan.noHeats')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─────────── assignation automatique ─────────── */}
      <Sheet open={autoOpen} onOpenChange={setAutoOpen}>
        <SheetContent
          side="right"
          hideClose
          className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[520px]"
        >
          <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <SheetTitle className="font-anton text-3xl font-normal uppercase leading-none">
                {t('judgePlan.autoTitle')}
              </SheetTitle>
              <SheetDescription className="text-xs">{t('judgePlan.autoIntro')}</SheetDescription>
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => setAutoOpen(false)}>
              <X size={16} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0 space-y-1.5">
                <Label>{t('judgePlan.autoRestAfter')}</Label>
                <NumberInput min={1} value={restAfter} onChange={(e) => { const n = parseInt(e.target.value, 10); if (n >= 1) setRestAfter(n); }} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label>{t('judgePlan.autoRestHeats')}</Label>
                <NumberInput min={0} value={restHeats} onChange={(e) => { const n = parseInt(e.target.value, 10); if (n >= 0) setRestHeats(n); }} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('judgePlan.autoJudges')} · {autoJuges.size}/{shown.length}</Label>
              {/* La disponibilité passe SOUS le nom et revient à la ligne : en
                  `truncate` sur la même ligne, son texte insécable élargissait
                  tout le contenu au-delà du cadre (intro, champ et boutons
                  coupés, 2026-09-29). */}
              <div className="divide-y divide-white/[0.06]">
                {shown.map((v) => (
                  <label key={v.id} className="flex cursor-pointer items-start gap-2.5 py-2 text-sm">
                    <Checkbox className="mt-0.5" checked={autoJuges.has(v.id)}
                      onCheckedChange={(on) => setAutoJuges((prev) => { const next = new Set(prev); if (on) next.add(v.id); else next.delete(v.id); return next; })} />
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf(v.id) }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{v.first_name} {v.last_name}</span>
                      <span className="block text-[11px] leading-snug text-muted-foreground">
                        {libelleDisponibilite(v.available_slots, i18n.language)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {autoJuges.size === 0 && <p className="text-xs text-warning">{t('judgePlan.autoNoJudge')}</p>}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-white/[0.08] px-6 py-4">
            <Button variant="ghost" onClick={() => setAutoOpen(false)}>{t('common.close')}</Button>
            <Button onClick={lancerAuto} disabled={autoBusy || autoJuges.size === 0}>
              {autoBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Wand2 size={15} className="mr-1.5" />}
              {t('judgePlan.autoPropose')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <ConfirmDialog
        open={confirmClearAll}
        onOpenChange={setConfirmClearAll}
        title={t('judgePlan.clear.allDaysTitle')}
        message={t('judgePlan.clear.allDaysMessage', { count: Object.keys(plan).length, days: days.length })}
        confirmLabel={t('judgePlan.clear.button')}
        destructive
        onConfirm={() => { viderTout(); setConfirmClearAll(false); }}
      />
    </div>
  );
};

export default JudgePlan;
